import React, { useEffect, useState } from 'react';
import { Col, Form, InputNumber, Row, Switch, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form';
import FormListAddButton from '../FormListAddButton';
import CredentialsInputs from '../Shared/Inputs/CredentialsInput';
import { ClientFeatureConfig } from '../../types/Feature';
import { DriversEtaAttributes } from '../../../../server/src/models/featureConfigAttributes/DriversEtaAttributes';
import FeatureStatusInput from '../Shared/Inputs/FeatureStatusInput';

interface DriversEtaFormSectionProps {
    formInstance: FormInstance;
    driversEtaConfig?: ClientFeatureConfig<DriversEtaAttributes>;
    isDetailMode?: boolean;
    enableControls?: boolean;
    resetFormFlag?: boolean;
}

interface DriversEtaConfig {
    fleetId: number;
    ssoUsername: string;
    ssoPassword: string;
}

interface FleetCredentialsListProps {
    driverEtas: DriversEtaConfig[];
    fieldKey: number;
    isDetailMode: boolean | undefined;
    remove: (fieldKey: number) => void;
}

const FleetCredentialsListItem: React.FC<FleetCredentialsListProps> = ({
    driverEtas,
    fieldKey,
    isDetailMode,
    remove,
}: FleetCredentialsListProps) => {
    return (
        <Row key={fieldKey}>
            <Col span={3}>
                <Form.Item name={[fieldKey, 'fleetId']}>
                    {isDetailMode ? (
                        <span>
                            Fleet ID:&nbsp;
                            {driverEtas?.[fieldKey]?.fleetId}
                        </span>
                    ) : (
                        <InputNumber title="Fleet id" />
                    )}
                </Form.Item>
            </Col>
            <Col span={20}>
                <CredentialsInputs
                    username={{
                        label: 'SSO Username',
                        name: [fieldKey, 'ssoUsername'],
                        value: driverEtas?.[fieldKey]?.ssoUsername,
                    }}
                    password={{
                        label: 'SSO Password',
                        name: [fieldKey, 'ssoPassword'],
                        value: driverEtas?.[fieldKey]?.ssoPassword,
                    }}
                    isDetailMode={isDetailMode || false}
                />
            </Col>
            {!isDetailMode && (
                <Col md={1}>
                    <span className="ant-form-text text-secondary" style={{ marginTop: '5px', cursor: 'pointer' }}>
                        <Tag
                            color="red"
                            style={{ cursor: 'pointer' }}
                            onClick={(): void => remove?.(fieldKey)}
                            id={`driversEta_delete_${fieldKey}`}
                        >
                            <DeleteOutlined />
                        </Tag>
                    </span>
                </Col>
            )}
        </Row>
    );
};

const defReducer = (config: DriversEtaAttributes) => (acc: DriversEtaConfig[], fleetId: string): DriversEtaConfig[] => {
    const deConfig = config[fleetId];
    if (fleetId !== 'enabled' && typeof deConfig === 'object') {
        acc.push({
            fleetId: Number(fleetId),
            ...deConfig,
        });
    }
    return acc;
};

const DriversEtaFormSection: React.FC<DriversEtaFormSectionProps> = (props: DriversEtaFormSectionProps) => {
    const { formInstance, driversEtaConfig, isDetailMode, resetFormFlag, enableControls } = props;

    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(false);
    const [driverEtas, setDriversEtas] = useState<DriversEtaConfig[]>([]);

    useEffect(() => {
        if (driversEtaConfig) {
            const driversEtas = Object.keys(driversEtaConfig.config).reduce(defReducer(driversEtaConfig.config), []);
            setDriversEtas(driversEtas);
        }
    }, [driversEtaConfig]);
    useEffect(() => {
        if (driversEtaConfig && driverEtas) {
            formInstance.setFields([
                { name: 'driversEtaEnabled', value: driversEtaConfig.config.enabled },
                { name: 'driversEtas', value: driverEtas },
            ]);
            setIsFeatureDisabled(!driversEtaConfig.config.enabled);
        }
    }, [driverEtas]);

    useEffect(() => {
        setIsFeatureDisabled(true);
    }, [resetFormFlag]);

    return (
        <div className="card border-0" id="fleets-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={{ fontSize: '18px' }}>
                    Drivers ETA fleet credentials
                </p>
                <div className="d-inline-flex align-items-center">
                    <span className="mr-2 d-none d-sm-block">Feature is:</span>
                    <FeatureStatusInput
                        name="driversEtaEnabled"
                        id="drivers_eta_enabled"
                        isDetailMode={isDetailMode}
                        enabled={driversEtaConfig?.config.enabled}
                        isFeatureDisabled={isFeatureDisabled}
                        onChange={(value): void => {
                            setIsFeatureDisabled(!value);
                        }}
                        enableControls={enableControls}
                    />
                </div>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.List name="driversEtas">
                            {(fields, { add, remove }): JSX.Element => (
                                <>
                                    {fields.map((field) => (
                                        <FleetCredentialsListItem
                                            driverEtas={driverEtas}
                                            fieldKey={field.key}
                                            key={field.key}
                                            remove={remove}
                                            isDetailMode={isDetailMode}
                                        />
                                    ))}
                                    <FormListAddButton
                                        add={add}
                                        text="Add fleet credentials"
                                        isDetailMode={isDetailMode || false}
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default DriversEtaFormSection;
